import { parseDateValue } from '../utils/formatUtils';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./SourceData.scss";
import { getAPIUrl } from "../api/apiUtils";

const SOURCES = [
  { id: "vk", title: "ВК" },
  { id: "tg", title: "Telegram" },
  { id: "news", title: "Новости" },
  { id: "youtube", title: "YouTube" },
];

const POSTS_LIMIT = 20;
const COMMENTS_LIMIT = 50;

const today = () => new Date().toISOString().slice(0, 10);

const formatDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const date = parseDateValue(value);
  if (!date) return "Нет даты";
  return date.toLocaleString("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const textFrom = (item) =>
  item?.text ||
  item?.message ||
  item?.comment ||
  item?.raw?.message ||
  item?.raw?.text ||
  item?.text_original ||
  item?.text_display ||
  item?.raw?.snippet?.textOriginal ||
  item?.raw?.snippet?.textDisplay ||
  item?.description ||
  item?.title ||
  "";

const counterValue = (value) => {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === "object") {
    if (value.count !== undefined) return counterValue(value.count);
    if (value.replies !== undefined) return counterValue(value.replies);
    if (Array.isArray(value.items)) return value.items.length;
    if (Array.isArray(value.results)) return value.results.reduce((sum, item) => sum + counterValue(item), 0);
    return 0;
  }
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const numberLabel = (value) => {
  const number = counterValue(value);
  return number > 0 ? number.toLocaleString("ru-RU") : "0";
};

const getPostImage = (post) => {
  const attachments = post?.raw?.attachments || post?.attachments || [];
  const photo = attachments.find((item) => item?.type === "photo" && item?.photo);
  const sizes = photo?.photo?.sizes || [];
  return sizes.find((item) => item.type === "x")?.url || sizes[sizes.length - 1]?.url || "";
};

const Pagination = ({ page, total, limit, onChange }) => {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  return (
    <div className="source-data-pagination">
      <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1}>
        Назад
      </button>
      <span>
        {page} / {totalPages}
      </span>
      <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
        Вперед
      </button>
    </div>
  );
};

const SourceData = () => {
  const [source, setSource] = useState("vk");
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postsPage, setPostsPage] = useState(1);
  const [commentsPage, setCommentsPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalComments, setTotalComments] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(today());
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState("");

  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.id === channelId),
    [channels, channelId]
  );

  const loadChannels = useCallback(async (nextSource) => {
    setLoadingChannels(true);
    setError("");
    setChannels([]);
    setChannelId("");
    setPosts([]);
    setComments([]);
    setSelectedPost(null);
    setTotalPosts(0);
    setTotalComments(0);

    try {
      const response = await axios.get(`${getAPIUrl()}/source_data/channels`, {
        params: { source: nextSource },
      });
      const nextChannels = response.data.channels || [];
      setChannels(nextChannels);
      setChannelId(nextChannels[0]?.id || "");
    } catch (requestError) {
      console.error("Failed to load source channels", requestError);
      setError("Не удалось загрузить список каналов");
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    if (!channelId) return;

    setLoadingPosts(true);
    setError("");

    try {
      const response = await axios.get(`${getAPIUrl()}/source_data/posts`, {
        params: {
          source,
          channel_id: channelId,
          page: postsPage,
          limit: POSTS_LIMIT,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      setPosts(response.data.items || []);
      setTotalPosts(response.data.total || 0);
    } catch (requestError) {
      console.error("Failed to load source posts", requestError);
      setError("Не удалось загрузить посты");
      setPosts([]);
      setTotalPosts(0);
    } finally {
      setLoadingPosts(false);
    }
  }, [channelId, endDate, postsPage, source, startDate]);

  const loadComments = useCallback(async () => {
    if (!selectedPost) {
      setComments([]);
      setTotalComments(0);
      return;
    }

    setLoadingComments(true);

    try {
      const response = await axios.get(`${getAPIUrl()}/source_data/comments`, {
        params: {
          source,
          channel_id: selectedPost.channel_id || channelId,
          post_id: selectedPost.id,
          page: commentsPage,
          limit: COMMENTS_LIMIT,
        },
      });
      setComments(response.data.comments || []);
      setTotalComments(response.data.total || 0);
    } catch (requestError) {
      console.error("Failed to load source comments", requestError);
      setComments([]);
      setTotalComments(0);
    } finally {
      setLoadingComments(false);
    }
  }, [channelId, commentsPage, selectedPost, source]);

  useEffect(() => {
    loadChannels(source);
  }, [loadChannels, source]);

  useEffect(() => {
    setPostsPage(1);
    setCommentsPage(1);
  }, [channelId, endDate, source, startDate]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    const currentPostStillVisible = posts.some((post) => post.id === selectedPost?.id);
    setSelectedPost(currentPostStillVisible ? selectedPost : posts[0] || null);
    setCommentsPage(1);
  }, [posts, selectedPost]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSourceChange = (nextSource) => {
    if (nextSource === source) return;
    setChannels([]);
    setChannelId("");
    setPosts([]);
    setComments([]);
    setSelectedPost(null);
    setTotalPosts(0);
    setTotalComments(0);
    setSource(nextSource);
    setPostsPage(1);
    setCommentsPage(1);
  };

  const handleChannelChange = (event) => {
    setChannelId(event.target.value);
  };

  const handleSelectPost = (post) => {
    setSelectedPost(post);
    setCommentsPage(1);
  };

  const renderPost = (post) => {
    const imageUrl = getPostImage(post);
    const text = post.text || post.title || "Нет текста";

    return (
      <button
        type="button"
        key={`${post.source}-${post.channel_id}-${post.id}`}
        className={`source-data-post ${selectedPost?.id === post.id ? "source-data-post--active" : ""}`}
        onClick={() => handleSelectPost(post)}
      >
        <div className="source-data-post__title">{post.title || text.slice(0, 120)}</div>
        {post.title && post.text ? <p>{post.text}</p> : null}
        {imageUrl ? <img src={imageUrl} alt="" className="source-data-post__image" /> : null}
        <div className="source-data-meta">
          <span>{formatDate(post.display_date || post.date)}</span>
          <span>{source === "vk" ? "Комментарии и ответы" : "Комментарии"}: {numberLabel(post.comments_count)}</span>
          <span>Лайки: {numberLabel(post.likes)}</span>
          <span>Просмотры: {numberLabel(post.views)}</span>
        </div>
      </button>
    );
  };

  const renderComment = (comment, index) => {
    const author =
      comment.author_name ||
      comment.author_display_name ||
      comment.author ||
      comment.user_name ||
      comment.raw?.snippet?.authorDisplayName ||
      comment.from_user_id ||
      comment.author_channel_id ||
      comment.from_id ||
      "Автор";

    return (
      <div key={comment.id || comment.comment_id || `${commentsPage}-${index}`} className={`source-data-comment${source === "vk" && comment.is_reply ? " source-data-comment--reply" : ""}`}>
        {source === "vk" && comment.is_reply ? <div className="source-data-reply-label">Ответ на комментарий №{comment.parent_comment_id}</div> : null}
        <p>{textFrom(comment) || "Нет текста"}</p>
        <div className="source-data-meta">
          <span>{formatDate(comment.display_date || comment.date || comment.published_at)}</span>
          <span>{author}</span>
          {comment.likes || comment.like_count ? <span>Лайки: {numberLabel(comment.likes || comment.like_count)}</span> : null}
        </div>
      </div>
    );
  };

  return (
    <div className="source-data">
      <div className="source-data-toolbar">
        <div className="source-data-source-tabs" aria-label="Источники">
          {SOURCES.map((item) => (
            <button
              type="button"
              key={item.id}
              className={source === item.id ? "source-data-tab source-data-tab--active" : "source-data-tab"}
              onClick={() => handleSourceChange(item.id)}
            >
              {item.title}
            </button>
          ))}
        </div>

        <label className="source-data-field">
          <span>Канал / источник</span>
          <select value={channelId} onChange={handleChannelChange} disabled={loadingChannels || channels.length === 0}>
            {channels.length === 0 ? <option value="">Нет данных</option> : null}
            {channels.map((channel) => (
              <option key={channel.id} value={channel.id}>
                {channel.title}
              </option>
            ))}
          </select>
        </label>

        <label className="source-data-field source-data-field--date">
          <span>С</span>
          <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </label>

        <label className="source-data-field source-data-field--date">
          <span>По</span>
          <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </label>

        <button type="button" className="source-data-refresh" onClick={loadPosts} disabled={!channelId || loadingPosts}>
          Обновить
        </button>
      </div>

      {selectedChannel ? (
        <div className="source-data-channel">
          <strong>{selectedChannel.title}</strong>
          {selectedChannel.subtitle ? <span>{selectedChannel.subtitle}</span> : null}
        </div>
      ) : null}

      {error ? <div className="source-data-error">{error}</div> : null}

      <div className="source-data-container">
        <section className="source-data-panel">
          <div className="source-data-panel__header">
            <h2>Посты</h2>
            <div className="source-data-panel__actions">
              <span>{totalPosts.toLocaleString("ru-RU")}</span>
            </div>
          </div>

          {loadingPosts ? <div className="source-data-empty">Загрузка постов...</div> : null}
          {!loadingPosts && posts.length === 0 ? <div className="source-data-empty">Постов не найдено</div> : null}
          {!loadingPosts && posts.length > 0 ? <div className="source-data-posts">{posts.map(renderPost)}</div> : null}

          <Pagination page={postsPage} total={totalPosts} limit={POSTS_LIMIT} onChange={setPostsPage} />
        </section>

        <section className="source-data-panel source-data-panel--details">
          <div className="source-data-panel__header">
            <h2>{source === "vk" ? "Пост, комментарии и ответы" : "Пост и комментарии"}</h2>
            <div className="source-data-panel__actions">
              <span>{totalComments.toLocaleString("ru-RU")}</span>
            </div>
          </div>

          {selectedPost ? (
            <>
              <article className="source-data-selected-post">
                <h3>{selectedPost.title || "Выбранный пост"}</h3>
                <p>{selectedPost.text || "Нет текста"}</p>
                {selectedPost.url ? (
                  <a href={selectedPost.url} target="_blank" rel="noopener noreferrer">
                    Открыть оригинал
                  </a>
                ) : null}
                <div className="source-data-meta">
                  <span>{formatDate(selectedPost.display_date || selectedPost.date)}</span>
                  <span>{source === "vk" ? "Комментарии и ответы" : "Комментарии"}: {numberLabel(selectedPost.comments_count)}</span>
                  <span>Лайки: {numberLabel(selectedPost.likes)}</span>
                  <span>Просмотры: {numberLabel(selectedPost.views)}</span>
                </div>
              </article>

              <div className="source-data-comments">
                {loadingComments ? <div className="source-data-empty">Загрузка комментариев...</div> : null}
                {!loadingComments && comments.length === 0 ? <div className="source-data-empty">Комментариев нет</div> : null}
                {!loadingComments && comments.length > 0 ? comments.map(renderComment) : null}
              </div>

              <Pagination page={commentsPage} total={totalComments} limit={COMMENTS_LIMIT} onChange={setCommentsPage} />
            </>
          ) : (
            <div className="source-data-empty">Выберите канал и пост</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SourceData;
